import { Tracker } from '@scm-italy/pulse-sdk-js';
import { createPromiseResolver, isMobile } from '@sbt-web/utils';
import { getHoustonCollector } from './getHoustonCollector/getHoustonCollector.js';

/** Default collector base URL (overwritten by Houston at runtime in prod). */
const COLLECTOR_URL = 'https://collector.subito.it';
/** Default collector path for ingestion API. */
const COLLECTOR_PATH = 'api/v1/track';
/** Default CIS base URL (Customer Identity Service). */
const CIS_BASE_URL = 'https://cis.mpianalytics.com';
/**
 * Base Pulse configuration used to initialize the SDK.
 * Will be partially overridden by Houston collector in production.
 */
const CONF = {
    // override default values. Get it from Houston collector:
    collectorBaseUrl: COLLECTOR_URL,
    cisBaseUrl: CIS_BASE_URL,
    collectorPath: COLLECTOR_PATH,
    eventDebounce: 200,
    environment: 'browser',
    useBeaconWhenAvailable: true,
};
/**
 * Lazily-initialized shared Pulse tracker instance promise.
 */
const [promiseTracker, resolveTracker] = createPromiseResolver();
let neverResolved = true;
/**
 * Pulse tracking client wrapper around the Pulse SDK.
 * Handles lazy initialization, environment normalization and simple API.
 *
 * @example
 * ```ts
 * import { Pulse } from '@sbt-web/tracking';
 *
 * const pulse = new Pulse();
 * ```
 */
class Pulse {
    isReady;
    constructor(env, userId) {
        const [initializedPromise, resolveInitialized] = createPromiseResolver();
        this.isReady = initializedPromise;
        this.initTracker(resolveInitialized, env, userId); //NOSONAR
    }
    /**
     * Initialize the underlying Pulse SDK and set initial context.
     * When demerge will be completed, please mark env as mandatory.
     *
     * @example
     * ```ts
     * import { initTracker } from '@sbt-web/tracking';
     *
     * const pulseInstance = await initTracker();
     * ```
     */
    async initTracker(resolveInitialized, env, userId) {
        const tracker = await this.initPulseInstance();
        // Set userId:
        const backCompatibilityUserId = userId ?? window.subito?.dataLayer?.user?.id;
        if (backCompatibilityUserId) {
            this.setActor(backCompatibilityUserId);
        }
        // set deploy stage and product type:
        if (env) {
            this.setEnv(env);
        }
        tracker.update({ provider: { productType: isMobile() ? 'M-Site' : 'Web' } }, true // This is needed to tell the SDK not to overwrite the existing object but to merge it
        );
        resolveInitialized(true);
    }
    /**
     * Lazily create or return the shared Pulse SDK tracker instance.
     *
     * @example
     * ```ts
     * import { initPulseInstance } from '@sbt-web/tracking';
     *
     * const pulseInstance = await initPulseInstance();
     * ```
     */
    async initPulseInstance() {
        if (neverResolved) {
            neverResolved = false;
            try {
                // Always use the Houston collector in prod:
                const collector = await getHoustonCollector('prod');
                // Remove trailing slash from collector URL:
                CONF.collectorBaseUrl = (collector.datacollector ?? '').replace(/\/$/, '');
            }
            catch {
                console.warn('Houston collector not found, using default');
            }
            // Define tracker:
            const tracker = new Tracker('subito', CONF, {});
            if (window?.subito) {
                window.subito.tracker = tracker;
            }
            resolveTracker(tracker);
        }
        return await promiseTracker;
    }
    /**
     * Set the current actor (user) in the tracking context.
     * @param userId - Numeric or string user identifier.
     *
     * @example
     * ```ts
     * import { setActor } from '@sbt-web/tracking';
     *
     * setActor('1234567890');
     * ```
     */
    async setActor(userId) {
        (await promiseTracker).update({
            actor: {
                '@type': 'Account',
                id: userId,
                realm: 'subito.it',
            },
        });
    }
    /**
     * Normalize and set the deploy stage/environment in the tracking context.
     * @param deployStageSubito - Environment value from app ('prod' | 'pre' | 'dev' | etc.).
     * @returns The normalized deploy stage or null if unknown.
     *
     * @example
     * ```ts
     * import { setEnv } from '@sbt-web/tracking';
     *
     * setEnv('prod');
     * ```
     */
    async setEnv(deployStageSubito) {
        const deployStage = normalizeEnvName(deployStageSubito);
        if (deployStage) {
            (await promiseTracker).update({ deployStage });
            return deployStage;
        }
        return null;
    }
    /**
     * Get the environment id from the SDK (asynchronously).
     *
     * @example
     * ```ts
     * import { getEnvironmentId } from '@sbt-web/tracking';
     *
     * const environmentId = await getEnvironmentId();
     * ```
     */
    async getEnvironmentId() {
        return (await promiseTracker).getEnvironmentId();
    }
    /**
     * Queue and send a tracking event.
     * Automatically starts a new page view when `data.type === 'View'`.
     * @param data - Event payload.
     * @param action - Event name/channel. Defaults to 'trackerEvent'.
     *
     * @example
     * ```ts
     * import { queueEvent } from '@sbt-web/tracking';
     *
     * queueEvent({ type: 'View' });
     * ```
     */
    async queueEvent(data, action = 'trackerEvent') {
        const tracker = await promiseTracker;
        if (data.type === 'View') {
            tracker.newPageView();
        }
        tracker.track(action, data).catch((e) => {
            console.warn('Pulse: The tracking failed with', e);
            return e;
        });
    }
    /**
     * Access the underlying Pulse SDK tracker instance.
     */
    async getTracker() {
        return await promiseTracker;
    }
}
/**
 * Normalize various env labels to the Pulse-supported deploy stage.
 * @param envName - Raw environment label from the app.
 * @returns Normalized deploy stage or null.
 *
 * @example
 * ```ts
 * import { normalizeEnvName } from '@sbt-web/tracking';
 *
 * const env = normalizeEnvName('prod'); // pro
 * ```
 */
function normalizeEnvName(envName) {
    switch (envName) {
        case 'prod':
        case 'pro':
            return 'pro';
        case 'pre':
        case 'stage':
        case 'integration':
            return 'pre';
        case 'dev':
        case 'regress':
            return 'dev';
        default:
            return null;
    }
}

export { Pulse };
//# sourceMappingURL=client.js.map
